<script lang="ts" setup>
import { computed, ref, onBeforeUnmount } from 'vue'
import { useDropZone, useFileDialog } from '@vueuse/core'
import { format } from 'quasar'
import { useFileUploadStore } from '../../stores/file-upload'

const { humanStorageSize } = format
const fileUploadStore = useFileUploadStore()
const noFileMessage = 'Please upload at least one media file.'
const hasUploadedFile = computed(() => fileUploadStore.uploadedFiles.length > 0)
// Hidden mid-upload: a file is on its way, so "upload at least one" would be wrong.
const showNoFileMessage = computed(() => !hasUploadedFile.value && !fileUploadStore.isUploading)
function validate() {
  return hasUploadedFile.value
}

defineExpose({ validate })

const objectUrlCache = new Map<string, string>()

onBeforeUnmount(() => {
  objectUrlCache.forEach((url) => URL.revokeObjectURL(url))
  objectUrlCache.clear()
})

// --- Dropzone Setup ---
const dropZoneRef = ref<HTMLDivElement>()
const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop: async (files) => {
    if (files && files.length > 0) {
      fileUploadStore.addFilesForUpload(Array.from(files))
      // Kick off the upload sequence immediately
      await fileUploadStore.tryUploadFiles()
    }
  },
  dataTypes: ['image/*', 'video/*']
})

// --- File Dialog Setup ---
const { open: openFileDialog, onChange: onFileDialogChange } = useFileDialog({
  accept: 'image/*, video/*',
  multiple: true
})

onFileDialogChange(async (files) => {
  if (files && files.length > 0) {
    fileUploadStore.addFilesForUpload(Array.from(files))
    // Kick off the upload sequence immediately
    await fileUploadStore.tryUploadFiles()
  }
})

function getStatusLabel(file: any): string {
  if (file.hasError) return file.errorMessage || 'Upload failed. Please try again.'
  if (file.isUploaded) return 'Scan complete. Ready for submission.'
  if (file.isUploading) return 'Uploading to secure storage...'
  return 'Awaiting background upload...'
}
</script>

<template>
  <q-form class="row q-col-gutter-y-md">
    <div class="col-12">
      <h6 class="text-h6 q-mt-none q-mb-sm text-blue-grey-9">Select your files</h6>
      <p class="text-body2 text-blue-grey-8">Include all videos and photos of the incident.</p>
    </div>

    <div class="col-12">
      <div
        ref="dropZoneRef"
        class="dropzone-container flex flex-center q-pa-lg rounded-borders transition-border cursor-pointer"
        :class="isOverDropZone ? 'bg-blue-1 border-blue' : 'bg-grey-1 border-dashed'"
        @click="openFileDialog()"
      >
        <div class="text-center">
          <q-icon
            :name="isOverDropZone ? 'mdi-archive-arrow-down' : 'mdi-cloud-upload-outline'"
            size="xl"
            :color="isOverDropZone ? 'blue-8' : 'blue-grey-5'"
            class="transition-color"
          />
          <div class="text-subtitle1 text-weight-bold q-mt-sm text-blue-grey-9">
            DRAG FILES HERE OR <span class="text-blue-8">[SELECT FILES]</span>
          </div>
          <div class="text-caption text-blue-grey-5 q-mt-xs">Supports MP4, MOV, JPG, PNG</div>
        </div>
      </div>
    </div>

    <div v-if="showNoFileMessage" class="col-12 text-caption text-negative" role="status">
      {{ noFileMessage }}
    </div>

    <div class="col-12 q-mt-md" v-if="fileUploadStore.hasFiles">
      <q-list class="q-gutter-y-md">
        <q-card
          v-for="file in fileUploadStore.userFileList"
          :key="file.internalId"
          flat
          bordered
          class="media-card overflow-hidden bg-white"
          :class="{ 'border-error': file.hasError }"
        >
          <div class="row no-wrap full-height">
            <div class="col-auto thumbnail-container relative-position bg-blue-grey-1">
              <div
                v-if="file.isUploading || (!file.isUploaded && !file.hasError)"
                class="lava-lamp-bg flex flex-center full-height full-width"
              >
                <q-spinner-rings color="white" size="2.5em" thickness="4" />
              </div>

              <div
                v-else-if="file.hasError"
                class="flex flex-center full-height full-width bg-red-1"
              >
                <q-icon name="mdi-alert-circle" color="negative" size="md" />
              </div>

              <q-img
                v-else-if="file.localThumbnailUrl"
                :src="file.localThumbnailUrl"
                fit="contain"
                :ratio="16 / 9"
                class="full-height full-width"
              />
              <!--
              <div v-else class="flex flex-center full-height full-width bg-blue-grey-8">
                <q-icon name="mdi-check-circle" color="white" size="md" />
              </div> -->

              <div class="text-caption text-blue-grey-6 text-weight-medium text-right">
                {{ humanStorageSize(file.uploadFileSize || 0) }}
                <span v-if="file.durationSeconds">
                  &bull; {{ Math.round(file.durationSeconds) }}s</span
                >
              </div>
            </div>

            <div class="col q-pa-sm flex column justify-between overflow-hidden">
              <div class="row items-start justify-between no-wrap">
                <div
                  class="text-subtitle2 text-weight-bold ellipsis q-pr-sm"
                  style="max-width: 75%"
                >
                  {{ file.uploadFileName }}
                </div>
                <div class="text-caption text-blue-grey-6 text-weight-medium text-right">
                  {{ humanStorageSize(file.uploadFileSize || 0) }}
                </div>
              </div>

              <div
                class="text-caption text-weight-medium ellipsis"
                :class="file.hasError ? 'text-negative' : 'text-blue-grey-6'"
              >
                {{ getStatusLabel(file) }}
              </div>
            </div>

            <div
              class="col-auto q-pa-sm flex column justify-between items-end border-left-subtle"
              style="min-width: 120px"
            >
              <q-checkbox
                v-model="file.isPrimary"
                @update:model-value="fileUploadStore.setPrimaryFile(file.internalId)"
                checked-icon="mdi-star"
                unchecked-icon="mdi-star-outline"
                color="blue-8"
                label="Primary"
                left-label
                dense
                class="text-caption text-weight-bold text-blue-grey-9"
              />
              <q-btn
                flat
                dense
                size="sm"
                color="blue-grey-5"
                icon="mdi-delete"
                label="Remove"
                @click="fileUploadStore.removeFile(file.internalId)"
              />
            </div>
          </div>

          <q-linear-progress
            v-if="file.isUploading || (!file.isUploaded && !file.hasError)"
            indeterminate
            color="blue-8"
            class="absolute-bottom"
            size="4px"
          />
          <q-linear-progress
            v-else-if="file.isUploaded"
            :value="1"
            color="positive"
            class="absolute-bottom"
            size="4px"
          />
          <q-linear-progress
            v-else-if="file.hasError"
            :value="1"
            color="negative"
            class="absolute-bottom"
            size="4px"
          />
        </q-card>
      </q-list>
    </div>
  </q-form>
</template>

<style scoped>
.dropzone-container {
  min-height: 160px;
}

.transition-border {
  transition:
    border 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
}

.transition-color {
  transition: color 0.2s ease-in-out;
}

.border-dashed {
  border: 2px dashed var(--q-blue-grey-3);
}

.border-blue {
  border: 2px solid var(--q-blue-8) !important;
}

.border-error {
  border-color: var(--q-negative) !important;
}

.border-left-subtle {
  border-left: 1px solid rgba(0, 0, 0, 0.05);
}

/* Strict CLS Prevention */
.media-card {
  height: 90px;
}

.thumbnail-container {
  width: 160px; /* Enforces 16:9 ratio vertically bounded to 90px height */
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
